import { MenuItem } from "@blueprintjs/core"
import CustomCallout from "components/Atoms/CustomCallout"
import CustomCheckbox from "components/Atoms/CustomCheckbox"
import CustomDateRange from "components/Atoms/CustomDateRange"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomTextArea from "components/Atoms/CustomTextArea"
import Note from "components/Atoms/Note"
import StringInput from "components/Atoms/StringInput"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FileUpload from "components/Molecules/FileUpload"
import FormController from "components/Molecules/FormController"
import { AppPermissions } from "constants/permissions"
import dayjs from "dayjs"
import { COMPANIES, DEPOTS } from "graphql/selector.queries.gql"
import useAgronomist from "hooks/useAgronomist"
import useAuth, { useCurrentDepartment, useFormDisabled } from "hooks/useAuth"
import useAuthorizationStrict from "hooks/useAuthorizationStrict"
import { cloneDeep, get, set, update } from "lodash-es"
import React, { useEffect, useState } from "react"
import { parseJson } from "utils/custom.utils"
import BenchmarkRate from "./BenchmarkRate"
import DemandFormRow from "./DemandFormRow"
import DemandSummary, { WholeSaleDemand } from "./DemandSummary"
import GroupVisibility from "./GroupVisibility"
import HarvestGradeForm from "./HarvestGradeForm"
import TimeSlotForm from "./TimeSlotForm"
import VarietyGroupSelector from "./VarietyGroupSelector"
import { useDepartmentDetailQuery } from "generated/graphql"

export default function SingleDemands() {
  const agronomist = useAgronomist()
  const { user } = useAuth()
  const configStr = user?.currentDepartment?.metadata
  const [idw, setidw] = useState(null)

  const currentDepartment = get(user, "currentDepartment", {})
  useEffect(() => {
    if ("id" in currentDepartment) {
      setidw(currentDepartment.id)
    }
  }, [currentDepartment])

  const { data } = useDepartmentDetailQuery({
    variables: {
      id: idw,
    },
    fetchPolicy: "network-only",
    skip: !idw,
  })

  const [demandDetails, setDemandDetails] = useState<any>({
    varietyGroup: {},
    depot: {},
    company: {},
    shouldConsolidate: false,
    // Broadcast in 15 minutes from the current time
    broadcastTime: dayjs().add(15, "minutes").toISOString(),
    gradePrices: [{}],
    timeSlots: [
      {
        timeSlot: {},
      },
    ],
  })

  useEffect(() => {
    if (data) {
      setDemandDetails(prev => ({
        ...prev,
        depot: data.departmentDetails.preferredDepot,
      }))
    }
  }, [data])

  const varietyGroupCodename = demandDetails?.varietyGroup?.codename
  useEffect(() => {
    if (configStr && varietyGroupCodename) {
      const config = parseJson(configStr)
      const varietyConfig = config?.demand?.[varietyGroupCodename]
      setDemandDetails(prev => ({
        ...prev,
        gradePrices: prev.gradePrices?.map(gp => ({
          ...gp,
          priceUnit: gp?.priceUnit?.id
            ? gp.priceUnit
            : varietyConfig?.priceUnit,
        })),
        timeSlots: prev.timeSlots?.map(ts => ({
          ...ts,
          quantityUnit: ts?.quantityUnit?.id
            ? ts.quantityUnit
            : varietyConfig?.quantityUnit,
        })),
      }))
    }
  }, [configStr, varietyGroupCodename])
  const [isPriceRange, changePriceRange] = useState(false)
  const [filter, changeFilter] = useState({
    groupVisibility: [],
    companyVisibility: [],
  })
  const departmentId = useCurrentDepartment()

  function handleChange(path, value) {
    const demandDetailsCopy = cloneDeep(demandDetails)
    set(demandDetailsCopy, path, value)
    setDemandDetails(demandDetailsCopy)
  }
  function handleAdd(path, value) {
    const demandDetailsCopy = cloneDeep(demandDetails)
    update(demandDetailsCopy, path, array => array.concat(value))
    setDemandDetails(demandDetailsCopy)
  }
  function handleRemove(path, index) {
    const demandDetailsCopy = cloneDeep(demandDetails)
    update(demandDetailsCopy, path, array =>
      array.filter((val, idx) => idx !== index)
    )
    setDemandDetails(demandDetailsCopy)
  }
  function handleFilterChange(path, value) {
    const filterCopy = cloneDeep(filter)
    set(filterCopy, path, value)
    changeFilter(filterCopy)
  }
  async function handlePriceRange(e) {
    changePriceRange(prevPrice => !prevPrice)
    changeFilter({
      groupVisibility: !isPriceRange ? [agronomist] : [],
      companyVisibility: [],
    })
  }

  const { timeSlots, gradePrices } = demandDetails

  const canConsolidateDemand = useAuthorizationStrict([
    AppPermissions.canConsolidateDemand,
  ])
  const disabled = useFormDisabled("demand")
  const isWholesale =
    isPriceRange &&
    (filter?.groupVisibility?.length >= 2 ||
      !filter?.groupVisibility?.find(group => group.id === agronomist?.id))

  return (
    <div className="w-full lg:max-w-[80%] mx-auto p-3">
      <div className="flex gap-8">
        <div className="w-2/3">
          <FormController
            disabled={disabled}
            disableAll={disabled}
            source={demandDetails}
            onChange={handleChange}
          >
            <DemandFormRow label="Demand Name">
              <StringInput
                id="demand_name"
                big={false}
                path="name"
                helperText="Max 150 characters."
              />
            </DemandFormRow>
            <DemandFormRow
              label="Demands"
              helperText="Demand for this commodity will be for the specific depot"
            >
              <div className="grid grid-cols-12 gap-x-8">
                <div className="col-span-8">
                  <VarietyGroupSelector />
                </div>
                <div className="col-span-4">
                  <DynamicSelector
                    id="demand_depot"
                    label="Depot"
                    path="depot"
                    query={DEPOTS}
                    variables={{ includeCommodity: true }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-12 gap-x-8">
                <div className="col-span-6">
                  <CustomDateRange
                    id="demand_date_range"
                    minDate={dayjs().toDate()}
                    maxDate={dayjs().add(7, "d").toDate()}
                    path="dateRange"
                    label="Demand Date Range"
                    big={false}
                  />
                </div>
                <div className="col-span-6">
                  <DynamicSelector
                    id="demand_company"
                    label="Company"
                    path="company"
                    query={COMPANIES}
                    variables={{
                      id: departmentId,
                    }}
                    itemRenderer={(item, { handleClick, modifiers }) => {
                      return (
                        <MenuItem
                          disabled={modifiers.disabled}
                          active={modifiers.active}
                          key={item.id}
                          onClick={handleClick}
                          text={get(item, "name")}
                          labelElement={get(item, "codename")}
                        />
                      )
                    }}
                  />
                </div>
                {canConsolidateDemand && (
                  <div className="col-span-12">
                    <Note
                      decolorize={!demandDetails?.shouldConsolidate}
                      textClassName={"flex items-center gap-1"}
                    >
                      <div>
                        <CustomCheckbox
                          white
                          className="mb-0"
                          value={demandDetails.shouldConsolidate}
                          onChange={(path, newVal) => {
                            setDemandDetails(prev => ({
                              ...prev,
                              [path]: newVal,
                            }))
                          }}
                          path="shouldConsolidate"
                        />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-base font-500">
                          Should Consolidate
                        </span>
                        <span>
                          if checked, all the daily demands will be consolidated
                          into a single demand
                        </span>
                      </div>
                    </Note>
                  </div>
                )}
              </div>
            </DemandFormRow>
            <DemandFormRow
              label="Grade"
              helperText="Pricing as per grades of commodity"
            >
              <CustomFormGroup
                className="mb-1"
                inline
                label="Enable Price Range ?"
              >
                <CustomCheckbox
                  id="demand_price_range"
                  value={isPriceRange}
                  name="isPriceRange"
                  onChange={handlePriceRange}
                />
              </CustomFormGroup>
              <HarvestGradeForm
                onRemove={handleRemove}
                onAdd={handleAdd}
                isPriceRange={isPriceRange}
                gradePrices={gradePrices}
              />
            </DemandFormRow>
            <DemandFormRow
              label="Time Slots"
              helperText="Tonnes required per slot"
            >
              <TimeSlotForm
                timeSlots={timeSlots}
                onRemove={handleRemove}
                onAdd={handleAdd}
              />
            </DemandFormRow>
            <DemandFormRow
              label="Attachment"
              helperText="Attach a single PDF document"
            >
              <FileUpload
                path="attachments"
                label="Attachments"
                multiple={false}
                accept="application/pdf"
              />
            </DemandFormRow>
            <DemandFormRow
              label="Remark"
              helperText="This remark is applied to every demand"
            >
              <CustomTextArea
                big={true}
                fill
                helperText="max 255 characters"
                path="description"
              />
            </DemandFormRow>
            <div className="grid grid-cols-12 gap-x-8">
              <div className="ml-auto col-span-12 flex justify-end">
                <DemandSummary
                  isPriceRange={isPriceRange}
                  isWholesale={isWholesale}
                  demandDetails={demandDetails}
                  filter={filter}
                />
              </div>
            </div>
          </FormController>
        </div>

        <div className="w-1/3">
          <CustomCallout
            className="border-t-2 border-t-green bg-green-50"
            title="Benchmark Rate"
          >
            <BenchmarkRate
              commodityGroup={demandDetails?.varietyGroup}
              first={7}
            />
          </CustomCallout>
          <FormController source={filter} onChange={handleFilterChange}>
            <CustomCallout className="mt-3" title="Visiblity Filter">
              <GroupVisibility />
            </CustomCallout>
            <CustomCallout className="mt-3" title="Constraints">
              <StringInput
                leftIcon="percentage"
                path="quantityTolerance"
                type="number"
                className="mt-3"
                big={false}
                label="Tolerance"
                fill={false}
                helperText="tolerance % to be applied on tonnage of every time slot"
              />
              <StringInput
                path="bookingQuantityLimit"
                type="number"
                className="mt-6"
                big={false}
                label="Booking Quantity Limit"
                fill={false}
                helperText="applied only for farmers"
              />
            </CustomCallout>
            {isWholesale && (
              <div className="mt-3">
                <WholeSaleDemand />
              </div>
            )}
          </FormController>
        </div>
      </div>
    </div>
  )
}
